import useConvertToJalaliDate from '@/composabels/useConvertToJalaliDate';
import { useToast } from '@/composabels/useToast';
import type { DynamicForm } from '@/types';
import numFa from '@/utils/num_fa';

import moment from 'moment-jalaali';
import REGEX from './regex';
function goToPaymentGateway(authority: string, isOpenInNewTab = false) {
  const targetUrl = 'https://www.zarinpal.com/pg/StartPay/';
  if (isOpenInNewTab) {
    window.open(`${targetUrl}${authority}`, '_blank');
  } else {
    window.location.href = `${targetUrl}${authority}`;
  }
}

function createValueObject(form: DynamicForm) {
  const newObject: Record<string, string> = {};
  for (const key of Object.keys(form)) {
    newObject[key] = form[key].value;
  }

  return newObject;
}
function extractOnlyNumbers(value: string): string {
  return value.replace(REGEX.ONLY_NUMBER, '');
}
const testRegexOnlyNumbers = (value: string) => {
  return REGEX.ONLY_NUMBER.test(value);
};
// extract number from string
const extractNumberFromString = (s = '') => {
  const elem = s
    .toString()
    .replace(REGEX.MATCH_PERSIAN_DIGITS, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString())
    .replace(REGEX.MATCH_ARABIC_DIGITS, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString())
    .replace(REGEX.NOT_DIGIT, '');
  return elem;
};

const convertPersianNumberToEnglish = (s = '', number?: boolean) => {
  const elem = s
    .toString()
    .replace(REGEX.MATCH_PERSIAN_DIGITS, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d) + '')
    .replace(REGEX.MATCH_ARABIC_DIGITS, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d) + '');
  if (number) return +elem;
  return elem;
};

function toEnNumber(payload: number | string): string {
  const modifiedToEnNumber = payload
    .toString()
    .replace(REGEX.MATCH_ARABIC_RANGE, (d: string) => (d.charCodeAt(0) - 1632).toString())
    .replace(REGEX.MATCH_PERSIAN_RANGE, (d: string) => (d.charCodeAt(0) - 1776).toString());
  return payload === '' ? payload : modifiedToEnNumber;
}
const seprateCommas = (amount: number | string) => {
  return toEnNumber(amount).replace(REGEX.THOUSANDS_SEPARATOR, ',');
};
function removeCommas(value: string): string {
  return value.replace(REGEX.COMMA, '');
}
function addToCalendar(startDate: string, startTime: string, eventTitle: string, eventDescription: string) {
  const [hours, minutes] = startTime.split(':').map(Number);

  const startDateTime = new Date(Date.UTC(Number(startDate.split('-')[0]), Number(startDate.split('-')[1]) - 1, Number(startDate.split('-')[2]), hours - 3, minutes - 30));

  const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

  const startDateTimeFormatted = startDateTime.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const endDateTimeFormatted = endDateTime.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${startDateTimeFormatted}/${endDateTimeFormatted}&text=${encodeURIComponent(
    eventTitle
  )}&details=${encodeURIComponent(eventDescription)}}`;
  window.open(calendarUrl, '_blank');
}

const copyText = (text: string) => {
  navigator.clipboard.writeText(text);
};

function downloadFile() {
  window.print();
}
function isDarkColor(hex: string): string {
  const r = parseInt(hex.substr(1, 2), 16);
  const g = parseInt(hex.substr(3, 2), 16);
  const b = parseInt(hex.substr(5, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5 ? '#ffffff' : '#000000';
}
function formatText(text: string) {
  return text.replace(REGEX.NEW_LINE, '<br>');
}

function roundNum(num: number, decimalPlaces: number): number {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(num * factor) / factor;
}
function getPlaceholder(key: string, defaultPlaceholder: string = ''): string {
  if (defaultPlaceholder) return defaultPlaceholder;

  switch (key.toLowerCase()) {
    case 'email':
      return 'ایمیل';
    case 'phone':
    case 'mobile':
      return 'تلفن';
    case 'name':
      return 'نام و نام خانوادگی';
    case 'description':
      return 'توضیحات';

    default:
      return ''; // Default to the key if no specific translation is provided
  }
}
function parsePath() {
  const path = window.location.pathname.replace(/\/+$/, '');
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 1) {
    const id = segments[0];
    const isPersonalLink = REGEX.ONLY_CHARACTER.test(id);

    if (isPersonalLink) {
      return id;
    }
  }

  if (segments.length > 1) {
    const id = segments.join('/');
    return id;
  }
  return null;
}
function parseRialToPersianText(value: string): string | null {
  const number = Number(removeCommas(value));
  if (Number.isNaN(number)) return '';
  const rialValue = number % 10;
  const amount = (number - rialValue) / 10;
  if (amount === 0 || amount < 100) return null;
  if (rialValue === 0 || rialValue < 100) {
    return numFa(Math.round(amount)) + ' تومان';
  } else {
    return numFa(amount) + ' و ' + numFa(rialValue) + ' تومان';
  }
}
const stringToBoolean = (string: string | undefined) => {
  string = string + '';
  switch (string?.toLowerCase()?.trim()) {
    case 'true':
    case 'yes':
    case '1':
      return true;
    case 'false':
    case 'no':
    case '0':
    case null:
      return false;
    default:
      return undefined;
  }
};
function formatCardPan(cardPan: string): string {
  return cardPan.match(REGEX.FORMAT_CARD_NUMBER)?.join('-') ?? cardPan;
}

function convertToJalaliDate(date: string, formatValue = 'HH:mm , jYYYY/jMM/jDD'): string {
  return moment(date).format(formatValue);
}
function convertDateToString(date: string, time?: string) {
  const { toJalali } = useConvertToJalaliDate();
  return time ? toJalali(date) + ' ' + ', ساعت ' + ' ' + time + ' ' : toJalali(date);
}
const { showToast } = useToast();

const handleCopyLink = async (url: string, customFun?: () => void, message: string = 'لینک رسید کپی شد') => {
  try {
    if (navigator?.clipboard && navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      showToast(message, 'success');
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError' && error.message === 'Permission denied') return;
    showToast('مرورگر شما مجاز به کپی نیست', 'error');
    return;
  } finally {
    customFun && customFun();
  }
};

const getBaseUrl = () => {
  const baseUrl = `${window.location.href}`;
  return `${baseUrl}`;
};

const addSpaceBetweenTextAndNumbers = (title: string) => {
  let result = title.replace(REGEX.ADD_SPACE_BETWEEN_CHARACTERS_AND_NUMBERS, '$1 $2');

  result = result.replace(REGEX.ADD_SPACE_BETWEEN_NUMBERS_AND_CHARACTERS, '$1 $2');

  result = result.replace(REGEX.ADD_SPACE_BETWEEN_PERSIAN_AND_LATIN, '$1 $2');

  result = result.replace(REGEX.ADD_SPACE_BETWEEN_LATIN_AND_PERSIAN, '$1 $2');
  return result;
};
const truncateText = (text: string, length: number) => (text.length > length ? text.slice(0, length) + '...' : text);
const formatUrlWithoutProtocol = (url: string) => url.replace(REGEX.URL_WITHOUT_PROTOCOL, '');
const scrollToErrorField = (errorField: HTMLElement) => {
  if (errorField) {
    errorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

export {
  goToPaymentGateway,
  createValueObject,
  extractNumberFromString,
  toEnNumber,
  seprateCommas,
  removeCommas,
  addToCalendar,
  convertPersianNumberToEnglish,
  isDarkColor,
  formatText,
  copyText,
  downloadFile,
  roundNum,
  getPlaceholder,
  parsePath,
  parseRialToPersianText,
  stringToBoolean,
  convertToJalaliDate,
  formatCardPan,
  convertDateToString,
  handleCopyLink,
  getBaseUrl,
  truncateText,
  formatUrlWithoutProtocol,
  extractOnlyNumbers,
  testRegexOnlyNumbers,
  addSpaceBetweenTextAndNumbers,
  scrollToErrorField,
};
